import {objectFromEntries} from '@benchling/js-utils';
import {DuotoneIconName, IconName} from '@benchling/nucleus-ui';

import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ReviewStatus} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  COMMENT as REVIEW_CONSTANTS_COMMENT,
  IN_PROGRESS_FILTER as REVIEW_CONSTANTS_IN_PROGRESS_FILTER,
  SENT_FOR_REVIEW as REVIEW_CONSTANTS_SENT_FOR_REVIEW,
} from 'notebook/reviews/ReviewConstants';
import {FolderItemIcon} from 'ui-library/icons/IconConstants';

/**
 * Maps from `BenchlingIcon` (monolith icon component) name to `Icon` (Nucleus UI icon component) name.
 *
 * Notes:
 * - "apple" mapping to "apple" is unnecessary. Identity will map to `Icon` automatically, if it is a valid
 *   icon name.
 * - When adding to this list, use `Sort Lines Ascending` on the whole list to ensure it is sorted
 *   consistently.
 *
 * @example
 *   // Entry ['apple', 'apple-whole'] means the following are equivalent:
 *   <BenchlingIcon name="apple" />
 *   <Icon name="apple-whole" />
 *
 * @package
 */
export const BENCHLING_ICON_ALIASES = [
  ['aa-analyze', 'protein-magnifying-glass'],
  ['add-collection', 'rectangle-history'],
  ['add-to-org', 'building-plus'],
  ['add-to-team', 'user-group-plus'],
  ['alignment', 'align-left'],
  ['analytics_analysis', 'lightbulb'],
  ['analyze', 'chart-column'],
  ['apple', 'apple-whole'],
  ['archive', 'box-archive-arrow-down'],
  ['arrow-first-page', 'chevrons-left'],
  ['arrow-last-page', 'chevrons-right'],
  ['arrow-up-down', 'arrow-up-arrow-down'],
  ['assembly-v2', 'circle-with-thick-segment'],
  ['assembly', 'arrows-rotate'],
  ['assign', 'user-arrow-right'],
  ['at-mention', 'at'],
  ['attachment', 'paperclip'],
  ['audit-trail', 'receipt-clock'],
  ['bacteria', 'bacterium'], // See 'cell-culture' -> 'bacteria'
  ['barcode', 'barcode-read'],
  ['benchling', 'benchling-logo'],
  ['blast', 'dna-magnifying-glass'],
  ['boolean', 'binary'],
  ['brackets', 'brackets-curly'],
  ['bulk-edit', 'pen-to-ellipsis-vertical'],
  ['bulk-select-next-container', 'tube-arrow-down'],
  ['calendar', 'calendar-days'],
  ['cancelled', 'ban'],
  ['cell-culture', 'bacteria'], // See 'bacteria' -> 'bacterium'
  ['chat', 'message-lines'],
  ['check-in-check-out', 'tube-check'],
  ['checklist', 'square-check'],
  ['chevron', 'chevron-right'],
  ['close', 'xmark-large'],
  ['cloud-upload', 'cloud-arrow-up'],
  ['collapse-down', 'square-caret-down'],
  ['collapse-right-full', 'arrow-down-left-and-arrow-up-right-to-center'],
  ['collapse', 'arrow-left'],
  ['collection', 'rectangle-history'],
  ['column-mapping', 'columns-three-location-dot'],
  ['columns', 'columns-three'],
  ['comfortable-view', 'distribute-spacing-vertical'],
  ['compact-view', 'arrows-to-line'],
  ['compose', 'plus-large'],
  ['computed-value', 'calculator-simple'],
  ['concatenation', 'chemical-concatenation'],
  ['console', 'rectangle-terminal'],
  ['converted-from', 'file-export'],
  ['core-object', 'apple-whole'],
  ['create', 'plus-large'],
  ['crispr', 'crosshairs'],
  ['cross-thick', 'xmark-large'],
  ['custom', 'asterisk'],
  ['dataset', 'file-chart-column'],
  ['decision-node', 'diamond'],
  ['delete', 'trash-can'],
  ['detect', 'crosshairs'],
  ['docs', 'file-lines'],
  ['document', 'file-lines'],
  ['done', 'check-circle'],
  ['dot', 'circle-small'],
  ['drag-handle', 'grip-dots-vertical'],
  ['edit', 'pen-to-square'],
  ['eln-timestamp', 'clock'],
  ['end-node', 'arrow-right-to-line'],
  ['entity-lookup', 'square-list-magnifying-glass'],
  ['enzyme', 'scissors'],
  ['erd', 'network-wired'],
  ['error', 'circle-xmark'],
  ['esign', 'signature'],
  ['exchange', 'arrow-right-arrow-left'],
  ['exclamation', 'exclamation-circle'],
  ['expand-right-full', 'arrow-up-right-and-arrow-down-left-from-center'],
  ['export', 'file-export'],
  ['external-files', 'folder-arrows-rotate'],
  ['fast-backward', 'backward-fast'],
  ['fast-forward', 'forward-fast'],
  ['feature-libraries', 'bookmark'],
  ['field', 'input-pipe'],
  ['file-browser-home', 'table-list'],
  ['fill-across-rows', 'arrow-zig-zag-right'],
  ['fill-down-columns', 'arrow-zig-zag-down'],
  ['filter-menu', 'bars-filter'],
  ['filter', 'sliders'],
  ['folder-parent', 'folder-open'],
  ['format-bold', 'bold'],
  ['format-bullet', 'list-ul'],
  ['format-calendar', 'calendar-days'],
  ['format-center', 'align-center'],
  ['format-checkbox', 'square-check'],
  ['format-delete', 'trash-can'],
  ['format-header', 'heading'],
  ['format-important', 'exclamation'],
  ['format-italic', 'italic'],
  ['format-left', 'align-left'],
  ['format-link', 'link'],
  ['format-number', 'list-ol'],
  ['format-print', 'print'],
  ['format-right', 'align-right'],
  ['format-strikethrough', 'strikethrough'],
  ['format-subscript', 'subscript'],
  ['format-superscript', 'superscript'],
  ['format-underline', 'underline'],
  ['fridge', 'refrigerator-outline'],
  ['hamburger', 'bars'],
  ['heatmap', 'game-board-simple'],
  ['helix', 'dna'],
  ['history', 'clock'],
  ['home-dashboard', 'house'],
  ['homology', 'chemical-homology'],
  ['hr-template', 'paste'],
  ['human', 'person'],
  ['import', 'file-import'],
  ['info', 'info-circle'],
  ['input', 'arrow-right-to-circle'],
  ['insert-from-inbox', 'file-import'],
  ['insert-instructions', 'list-arrow-down'],
  ['integration', 'plug'],
  ['intersect', 'object-intersect'],
  ['invite', 'envelope'],
  ['json', 'brackets-curly'],
  ['key', 'key-skeleton'],
  ['ladder', 'chemical-ladder'],
  ['line-chart', 'chart-line'],
  ['magic-wand', 'wand-magic-sparkles'],
  ['monomer', 'puzzle-piece'],
  ['more-info', 'circle-question'],
  ['mouse', 'mouse-field'],
  ['move-box', 'box-arrow-right'],
  ['move-to-inbox', 'file-export'],
  ['move', 'folder-arrow-right'],
  ['multi-select', 'list'],
  ['none-option', 'circle-xmark'],
  ['note-attach', 'paperclip'],
  ['note-code', 'code'],
  ['note-symbol', 'omega'],
  ['note-table', 'table'],
  ['note-text', 'plus'],
  ['note', 'pen-to-square'],
  ['notifications', 'bell'],
  ['number', 'hashtag'],
  ['open', 'arrow-up-right-to-square'],
  ['options', 'ellipsis-vertical'],
  ['organization', 'building'],
  ['overflow', 'ellipsis'],
  ['override-validation', 'file-circle-check'],
  ['panel-hidden', 'xmark'],
  ['parameters', 'square-sliders-outline'],
  ['pie-chart', 'chart-pie'],
  ['pin', 'thumb-tack'],
  ['plant', 'seedling'],
  ['previous-version', 'clock-rotate-left'],
  ['process-output', 'arrow-right-from-circle'],
  ['process-task', 'arrow-right-to-circle'],
  ['protein-alternative', 'protein-alt'],
  ['protocol-aggregate-time', 'clock'],
  ['protocol-critical-step', 'exclamation-triangle'],
  ['protocol-desc', 'align-left'],
  ['protocol-header', 'heading'],
  ['protocol-image', 'image'],
  ['protocol-pause-point', 'stop'],
  ['protocol-step', 'list-ol'],
  ['protocol-table', 'table'],
  ['protocol-timer', 'clock'],
  ['rack', 'server'],
  ['refresh', 'arrows-rotate'],
  ['register', 'arrow-right-to-grid-dots'],
  ['remove-contents', 'tube-emptying'],
  ['request-executed', 'check-circle-outline'],
  ['request-failed', 'circle-xmark-outline'],
  ['request-in-progress', 'timer-outline'],
  ['request-pending', 'circle-outline'],
  ['request-scheduled', 'clock-outline'],
  ['request', 'clipboard-check'],
  ['review_lifecycles', 'clipboard-list'],
  ['revision', 'exclamation-circle'],
  ['rna-sequence', 'rna'],
  ['rotate-left', 'arrow-rotate-left'],
  ['rotate-right', 'arrow-rotate-right'],
  ['run-query', 'play'],
  ['save', 'floppy-disk'],
  ['scan', 'barcode-read'],
  ['scatter-plot', 'chart-scatter'],
  ['search-filter-add', 'plus-large'],
  ['search-filter-remove', 'minus-large'],
  ['search', 'magnifying-glass'],
  ['send', 'paper-plane'],
  ['settings', 'gear'],
  ['share-alt', 'share-from-square'],
  ['share-v2', 'arrow-up-right-from-square'],
  ['shelf', 'shelves'],
  ['sign-out', 'right-from-bracket'],
  ['single-select', 'circle-caret-down'],
  ['skip', 'forward'],
  ['snapshot', 'camera-viewfinder'],
  ['sort-ascending-deprecated', 'arrow-up-wide-short'],
  ['sort-column', 'bars-sort'],
  ['sort-descending-deprecated', 'arrow-down-wide-short'],
  ['sources', 'nodes-converge'],
  ['spacer', 'square-caret-left-caret-right-outline'],
  ['split-workspace', 'table-columns'],
  ['square-corners', 'expand'],
  ['start-node', 'circle-play'],
  ['status-failed', 'circle-xmark'],
  ['status-success', 'check-circle'],
  ['status-warning', 'exclamation-triangle'],
  ['sub-item', 'corner'], // Callers will need to apply style: "transform: 'rotate(180deg)'"
  ['subtemplate', 'objects-column'],
  ['swap', 'arrow-right-arrow-left'],
  ['table-merge', 'columns-arrow-right-arrow-left'],
  ['team', 'users'],
  ['text', 'font-case'],
  ['times', 'xmark'],
  ['toggle', 'square-check'],
  ['transfer', 'tube-arrow-right'],
  ['translation', 'protein-alt'],
  ['triangle-down', 'caret-down'],
  ['triangle-right', 'caret-right'],
  ['tutorial-complete', 'check'],
  ['tutorial-default', 'file'],
  ['tutorial', 'graduation-cap'],
  ['unarchive', 'box-archive-arrow-up'],
  ['undo', 'arrow-rotate-left'],
  ['unit', 'ruler-combined'],
  ['unknown', 'question'],
  ['unregister', 'arrow-right-from-grid-dots'],
  ['validated-cloud', 'cloud-check'],
  ['validation', 'list-check-xmark'],
  ['warehouse-table', 'warehouse-full'],
  ['warning', 'exclamation-triangle'],
  ['worklist-add', 'list-plus'],
  [`${FolderItemIcon.BasicFolderItem}` as const, 'square-list'],
  [`${FolderItemIcon.Compound}` as const, 'chemical-compound'],
  [`${FolderItemIcon.Entry}` as const, 'file'],
  [`${FolderItemIcon.Linear}` as const, 'minus-large'],
  [`${FolderItemIcon.Material}` as const, 'beaker'],
  [`${FolderItemIcon.Mixture}` as const, 'ball-pile'],
  [`${FolderItemIcon.Molecule}` as const, 'ochem-molecule'],
  [`${FolderItemIcon.OligoV2}` as const, 'oligo-alt'],
  [`${FolderItemIcon.Plasmid}` as const, 'circle-notch'],
  [`${FolderItemIcon.PrimerPair}` as const, 'arrow-right-arrow-left'],
  [`${FolderItemIcon.Recipe}` as const, 'list'],
  [`${FolderItemIcon.RNASequence}` as const, 'rna'],
  [`${FolderItemIcon.SampleSlide}` as const, 'microscope'],
  [`${FolderItemIcon.Sequence}` as const, 'dna'],
  [`${FolderItemIcon.SmallMolecule}` as const, 'ochem-molecule'],
  [`${FolderItemIcon.SmallMoleculeBeaker}` as const, 'ochem-molecule-beaker'],
  [`${FolderItemIcon.Solution}` as const, 'beaker'],
  [`${ModelTypeConstants.ANALYSIS}` as const, 'lightbulb'],
  [`${ModelTypeConstants.ANALYSIS_TEMPLATE}` as const, 'lightbulb'],
  [`${ModelTypeConstants.ANALYTICS_DASHBOARD}` as const, 'chart-column'],
  [`${ModelTypeConstants.ANNOTATION}` as const, 'bookmark'],
  [`${ModelTypeConstants.ASSAY_PROCEDURE}` as const, 'eye-dropper'],
  [`${ModelTypeConstants.ASSAY_RESULT}` as const, 'table'],
  [`${ModelTypeConstants.ASSAY_RUN}` as const, 'instrument'],
  [`${ModelTypeConstants.ASSAY_RUN_TASK}` as const, 'arrow-right-to-circle'],
  [`${ModelTypeConstants.ASSAY_SCHEMA}` as const, 'table'],
  [`${ModelTypeConstants.ASSAY_STEP_TASK}` as const, 'arrow-right-to-circle'],
  [`${ModelTypeConstants.ASSAY_UNIT_OPERATION_TASK}` as const, 'arrow-right-to-circle'],
  [`${ModelTypeConstants.AUTOMATION_OUTPUT_FILE}` as const, 'instrument'],
  [`${ModelTypeConstants.BATCH_SCHEMA}` as const, 'flask'],
  [`${ModelTypeConstants.BATCH}` as const, 'flask'],
  [`${ModelTypeConstants.BENCHLING_APP}` as const, 'code'],
  [`${ModelTypeConstants.BIOENTITY_MIXIN}` as const, 'square-list'],
  [`${ModelTypeConstants.BLOB}` as const, 'paperclip'],
  [`${ModelTypeConstants.BOX_SCHEMA}` as const, 'box-archive'],
  [`${ModelTypeConstants.BOX}` as const, 'box-archive'],
  [`${ModelTypeConstants.BULK_ASSEMBLY}` as const, 'circle-with-thick-segment'],
  [`${ModelTypeConstants.CHART}` as const, 'chart-scatter'],
  [`${ModelTypeConstants.CONNECTION}` as const, 'plug'],
  [`${ModelTypeConstants.CONTAINER_SCHEMA}` as const, 'tube-empty'],
  [`${ModelTypeConstants.CONTAINER}` as const, 'tube-empty'],
  [`${ModelTypeConstants.DATA_FRAME}` as const, 'table'],
  [`${ModelTypeConstants.ENTITY}` as const, 'user'],
  [`${ModelTypeConstants.STAGE_CONFIGURATION}` as const, 'pen-to-square'],
  [`${ModelTypeConstants.STAGE_ENTRY}` as const, 'pen-to-square'],
  [`${ModelTypeConstants.EXTERNAL_FILE}` as const, 'paperclip'],
  [`${ModelTypeConstants.FILE}` as const, 'paperclip'],
  [`${ModelTypeConstants.FORM_DEFINITION}` as const, 'clipboard-list'],
  [`${ModelTypeConstants.JELLYFISH}` as const, 'benchling-logo'],
  [`${ModelTypeConstants.LOCATION_SCHEMA}` as const, 'refrigerator-outline'],
  [`${ModelTypeConstants.LOCATION}` as const, 'refrigerator-outline'],
  [`${ModelTypeConstants.NOTE_LINKED_FILE}` as const, 'paperclip'],
  [`${ModelTypeConstants.PLATE_SCHEMA}` as const, 'well-plate'],
  [`${ModelTypeConstants.PLATE}` as const, 'well-plate'],
  // If we're not sure what type of procedure it is, use list as the default
  [`${ModelTypeConstants.PROCEDURE}` as const, 'list'],
  [`${ModelTypeConstants.PROCESS_OUTPUT_SCHEMA}` as const, 'arrow-right-from-circle'],
  [`${ModelTypeConstants.PROCESS_OUTPUT}` as const, 'arrow-right-from-circle'],
  [`${ModelTypeConstants.PROCESS_SCHEMA}` as const, 'process'],
  [`${ModelTypeConstants.PROCESS_TASK_SCHEMA}` as const, 'arrow-right-to-circle'],
  [`${ModelTypeConstants.PROCESS_TASK}` as const, 'arrow-right-to-circle'],
  [`${ModelTypeConstants.PROJECT}` as const, 'briefcase'],
  [`${ModelTypeConstants.PROTEIN_ALIGNMENT}` as const, 'align-left'],
  [`${ModelTypeConstants.PROTOCOL}` as const, 'square-list'],
  [`${ModelTypeConstants.RECIPE_PROCEDURE}` as const, 'mortar-pestle'],
  [`${ModelTypeConstants.RECIPE_RUN_TASK}` as const, 'arrow-right-to-circle'],
  [`${ModelTypeConstants.RECIPE_STEP_TASK}` as const, 'arrow-right-to-circle'],
  [`${ModelTypeConstants.RECIPE_UNIT_OPERATION_TASK}` as const, 'arrow-right-to-circle'],
  [`${ModelTypeConstants.REGISTRY}` as const, 'grid-dots'],
  [`${ModelTypeConstants.REQUEST_SCHEMA}` as const, 'clipboard-check'],
  [`${ModelTypeConstants.REQUEST_TASK_SCHEMA}` as const, 'square-bracket-check-equals'],
  [`${ModelTypeConstants.REQUEST}` as const, 'clipboard-check'],
  [`${ModelTypeConstants.REQUEST_V2_DEFINITION}` as const, 'request-template'],
  [`${ModelTypeConstants.REQUEST_V2_SUBMISSION}` as const, 'notes-medical'],
  [`${ModelTypeConstants.RESULT_SCHEMA}` as const, 'table'],
  [`${ModelTypeConstants.RUN_SCHEMA}` as const, 'instrument'],
  [`${ModelTypeConstants.SAMPLE}` as const, 'flask'],
  [`${ModelTypeConstants.SAMPLE_DEFINITION}` as const, 'flask'],
  [`${ModelTypeConstants.SAVED_SEARCH}` as const, 'magnifying-glass'],
  [`${ModelTypeConstants.SCHEMA_FIELD_SELECTOR_OPTION}` as const, 'table-list'],
  [`${ModelTypeConstants.SCHEMA_FIELD_SELECTOR}` as const, 'ballot-check'],
  [`${ModelTypeConstants.SCHEMA_FIELD}` as const, 'table-list'],
  [`${ModelTypeConstants.SCHEMA_INTERFACE}` as const, 'object-intersect'],
  [`${ModelTypeConstants.SCRATCH_FILE}` as const, 'paperclip'],
  [`${ModelTypeConstants.SEQUENCE_ANALYSIS}` as const, 'align-left'],
  [`${ModelTypeConstants.SEQUENCE_PRIMER}` as const, 'oligo'],
  [`${ModelTypeConstants.SOURCE}` as const, 'briefcase'],
  [`${ModelTypeConstants.STUDY}` as const, 'book-open-cover'],
  [`${ModelTypeConstants.STUDY_SCHEMA}` as const, 'history-chart'],
  [`${ModelTypeConstants.TAG_SCHEMA}` as const, 'grid-dots'],
  [`${ModelTypeConstants.TEMPLATE_COLLECTION}` as const, 'rectangle-history'],
  [`${ModelTypeConstants.TENTACLE}` as const, 'leaf'],
  [`${ModelTypeConstants.TEST_DEFINITION}` as const, 'receipt-flask'],
  [`${ModelTypeConstants.TEST_ORDER}` as const, 'flask-receipt'],
  [`${ModelTypeConstants.TREATMENT_GROUP}` as const, 'flask'],
  [`${ModelTypeConstants.WORKLIST}` as const, 'list-asterisk'],
  [`${ModelTypeConstants.WORKSHEET}` as const, 'file-lines'],
  [`${REVIEW_CONSTANTS_COMMENT}` as const, 'comment'],
  [`${REVIEW_CONSTANTS_IN_PROGRESS_FILTER}` as const, 'timer'],
  [`${REVIEW_CONSTANTS_SENT_FOR_REVIEW}` as const, 'clipboard-question'],
  [`${ReviewStatus.ACCEPTANCE_SNAPSHOT_IN_PROGRESS}` as const, 'file-circle-check'],
  [`${ReviewStatus.ACCEPTED}` as const, 'file-circle-check'],
  [`${ReviewStatus.ACTION_REQUIRED}` as const, 'exclamation-triangle'],
  [`${ReviewStatus.NEEDS_REVIEW}` as const, 'clipboard-list'],
  [`${ReviewStatus.REJECTED}` as const, 'user-pen'],
  [`${ReviewStatus.RETRACTED}` as const, 'arrow-rotate-left'],
  [`${ReviewStatus.REVIEW_SNAPSHOT_IN_PROGRESS}` as const, 'clipboard-list'],
  // TODO(BNCH-121653): Clean these up once new flags are set
  [`${ModelTypeConstants.TEMPLATE}-in-workspace` as const, 'file-in-brackets'],
  [`${ModelTypeConstants.SUBTEMPLATE}-in-workspace` as const, 'broken-columns-in-bracket'],
  [`${ModelTypeConstants.FORM_DEFINITION}-in-workspace` as const, 'clipboard-in-brackets'],
  [`${ModelTypeConstants.REQUEST_V2_DEFINITION}-in-workspace` as const, 'stubbed-plus-sign-in-brackets'],
  [`${ModelTypeConstants.ANALYSIS_TEMPLATE}-in-workspace` as const, 'lightbulb-in-brackets'],
] as const satisfies ReadonlyArray<Readonly<[string, IconName | DuotoneIconName]>>;

/** @package */
export type IconAliasName = (typeof BENCHLING_ICON_ALIASES)[number][0];

/** @package */
export const BENCHLING_ICON_ALIAS_HASH: Record<IconAliasName, IconName> = objectFromEntries(
  BENCHLING_ICON_ALIASES as unknown as Array<[IconAliasName, IconName]>
);

/** @package */
export const ICON_ALIAS_NAMES_SET: Set<IconAliasName> = new Set(
  Object.keys(BENCHLING_ICON_ALIAS_HASH)
) as Set<IconAliasName>;

const FLAGGED_COLLECTIBLES: Array<string> = [
  ModelTypeConstants.TEMPLATE,
  ModelTypeConstants.SUBTEMPLATE,
  ModelTypeConstants.FORM_DEFINITION,
  ModelTypeConstants.REQUEST_V2_DEFINITION,
  ModelTypeConstants.ANALYSIS_TEMPLATE,
];

export function isTemplateCollectibleAlias(alias: string): boolean {
  return BenchlingConfig.ENABLE_TEMPLATE_COLLECTIONS_IN_WORKSPACE && FLAGGED_COLLECTIBLES.includes(alias);
}

/** @package */
export function isIconAlias(name: string): name is IconAliasName {
  // TODO(BNCH-121653): Clean this up once new flags are set
  if (isTemplateCollectibleAlias(name)) {
    return true;
  }
  return ICON_ALIAS_NAMES_SET.has(name as IconAliasName);
}

/** @package */
export function resolveIconAlias(alias: string): string {
  // TODO(BNCH-121653): Clean this up once new flags are set
  let modifiedAlias = alias;
  if (isTemplateCollectibleAlias(alias)) {
    modifiedAlias = `${alias}-in-workspace`;
  }

  if (!isIconAlias(modifiedAlias)) {
    throw new Error(`Invalid icon alias: ${modifiedAlias}`);
  }

  return BENCHLING_ICON_ALIAS_HASH[modifiedAlias];
}
